import { useLazyQuery } from "@apollo/client";
import { gql } from "@src/__generated__";
import { DocumentFieldsFragment } from "@src/__generated__/graphql";
import {
  Box,
  Button,
  SearchIcon,
  Spinner,
  TextInput,
  ViewIcon,
} from "@src/components/elements";
import { Checkbox } from "@src/components/elements/Checkbox";
import { DocumentPreview } from "@src/components/modules/DocumentForm/DocumentPreview";
import { UserName } from "@src/components/modules/UserName";
import { getFormattedDate } from "@src/utils/date";
import { debounce } from "lodash";
import React, { useMemo, useState } from "react";
import styled from "styled-components/macro";

const StyledRoot = styled.div<{ expanded: boolean }>`
  .search {
    width: 1.75rem;
    height: auto;
  }
  .search-results {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    overflow-y: auto;
    flex-grow: 1;

    .search-result {
      &:hover .full .preview-button {
        opacity: 1;
      }

      .full {
        display: ${({ expanded }) => (expanded ? "flex" : "none")};
        align-items: center;
        gap: 1rem;
        position: relative;

        .preview-button {
          transition: opacity 0.3s ease;
          opacity: 0;
          position: absolute;
          z-index: 1;
          top: 0.5rem;
          left: 2.75rem;
        }

        .preview {
          min-width: 40%;
          height: 100%;

          object {
            overflow: hidden;
            user-select: none;
            pointer-events: none;
          }
        }

        .info {
          display: grid;
          grid-template-columns: auto auto;
          grid-template-rows: auto;

          .label {
            color: var(--gray);
            text-align: right;
          }
          .value {
            font-weight: var(--font-weight-medium);
          }
        }
      }

      .simple {
        display: ${({ expanded }) => (expanded ? "none" : "flex")};
        align-items: center;
        gap: 1rem;
      }
    }
  }
`;

const SEARCH_DOCUMENTS = gql(`
  query SearchDocuments($searchTerm: String) {
    documents(displayName: $searchTerm) {
      edges {
        cursor
        node {
          ...DocumentFields
        }
      }
    }
  }
`);

const useDocumentSearch = ({
  onSearchResultSelected,
  onSearchResultDeselected,
}: {
  onSearchResultSelected: (document: DocumentFieldsFragment) => void;
  onSearchResultDeselected: (document: DocumentFieldsFragment) => void;
}) => {
  const [search, { loading: searchLoading, error: searchError }] =
    useLazyQuery(SEARCH_DOCUMENTS);

  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState<
    DocumentFieldsFragment[] | undefined
  >();

  function selectSearchResult(
    doc: DocumentFieldsFragment & { selected?: boolean },
  ) {
    if (searchResults) {
      setSearchResults(
        searchResults.map((d) =>
          d.id === doc.id ? { ...d, selected: true } : d,
        ),
      );
    }
    // Also add the doc to attached docs
    onSearchResultSelected(doc);
  }

  function deselectSearchResult(
    doc: DocumentFieldsFragment & { selected?: boolean },
  ) {
    if (searchResults) {
      setSearchResults(
        searchResults.map((d) =>
          d.id === doc.id ? { ...d, selected: false } : d,
        ),
      );
    }
    onSearchResultDeselected(doc);
  }

  const debounced = useMemo(
    () =>
      debounce(async (searchTerm: string) => {
        if (!searchTerm) {
          setSearchResults(undefined);
          return;
        }

        const result = await search({
          variables: { searchTerm },
        });

        if (result.data?.documents) {
          setSearchResults(
            result.data.documents.edges.map((d) => ({
              ...d.node,
            })),
          );
        }
      }, 500),
    [search],
  );

  function searchDocs(term: string) {
    setSearchTerm(term);
    void debounced(term);
  }

  return {
    searchResults,
    searchLoading,
    searchError,
    searchTerm,
    setSearchTerm,
    selectSearchResult,
    deselectSearchResult,
    debounced,
    searchDocs,
  };
};

type Props = {
  onDocumentClick: (document: DocumentFieldsFragment) => void;
  onSearchResultSelected: (document: DocumentFieldsFragment) => void;
  onSearchResultDeselected: (document: DocumentFieldsFragment) => void;
  selectedDocumentIds: string[];
  expanded: boolean;
};

export const DocumentSearch: React.FC<Props> = ({
  onDocumentClick,
  onSearchResultDeselected,
  onSearchResultSelected,
  selectedDocumentIds,
  expanded,
}) => {
  const {
    searchResults,
    searchLoading,
    searchError,
    searchTerm,
    selectSearchResult,
    deselectSearchResult,
    searchDocs,
  } = useDocumentSearch({
    onSearchResultSelected,
    onSearchResultDeselected,
  });
  return (
    <StyledRoot expanded={expanded}>
      <TextInput
        name="search"
        type="search"
        onChange={(event) => {
          searchDocs(event.target.value);
        }}
        prefix={<SearchIcon className="search" />}
        placeholder="Search for documents"
        value={searchTerm}
      />
      <>
        {searchLoading && (
          <div>
            <Spinner />
          </div>
        )}
        {searchError && (
          <div>
            <span className="error">Error: </span>
            {searchError.message}
          </div>
        )}
        {searchResults && searchResults.length === 0 && <div>No results</div>}
        {searchResults && searchResults.length ? (
          <>
            <p className="flush">Search results</p>
            <div className="search-results">
              {searchResults.map((doc, index) => {
                const selected = selectedDocumentIds.includes(doc.id);
                return (
                  <Box
                    key={index}
                    className="box margin-top row spread font-small search-result"
                    size="small"
                  >
                    <>
                      <div className="full">
                        <Button
                          variant="tertiary"
                          className="preview-button"
                          onClick={() => onDocumentClick(doc)}
                        >
                          <ViewIcon className="view-icon" />
                        </Button>
                        <Checkbox
                          label=""
                          onClick={() => {
                            if (selected) {
                              deselectSearchResult(doc);
                            } else {
                              selectSearchResult(doc);
                            }
                          }}
                          value={selected}
                          canShowError={false}
                        />
                        <div className="preview">
                          <DocumentPreview document={doc} />
                        </div>
                        <div className="column info">
                          <div className="label">Name</div>
                          <div className="value">{doc.displayName}</div>

                          <div className="label">Uploaded on</div>
                          <div className="value">
                            {getFormattedDate(doc.createdAt)}
                          </div>

                          <div className="label">Uploaded by</div>
                          <div className="value">
                            {doc.uploadedByUser ? (
                              <UserName user={doc.uploadedByUser} />
                            ) : (
                              <>&mdash;</>
                            )}
                          </div>
                          {doc.relatedServices.length ? (
                            <>
                              <div className="label">Related services</div>
                              <div className="value">
                                {doc.relatedServices.map(
                                  (service) => `${service.name}, `,
                                )}
                              </div>
                            </>
                          ) : null}
                          {doc.vendor ? (
                            <>
                              <div className="label">Associated vendor</div>
                              <div className="value">{doc.vendor.name}</div>
                            </>
                          ) : null}
                        </div>
                      </div>
                      <div className="simple">
                        <Checkbox
                          label=""
                          onChange={() => {
                            if (selected) {
                              deselectSearchResult(doc);
                            } else {
                              selectSearchResult(doc);
                            }
                          }}
                          value={selected}
                        />
                        <ViewIcon
                          className="view-icon"
                          onClick={() => onDocumentClick(doc)}
                        />
                        <div
                          className="label"
                          onClick={() => onDocumentClick(doc)}
                        >
                          {doc.displayName}
                        </div>
                      </div>
                    </>
                  </Box>
                );
              })}
            </div>
          </>
        ) : null}
      </>
    </StyledRoot>
  );
};
